import { useRef, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useAuthActions } from '@convex-dev/auth/react';
import {
  ChevronDown,
  ChevronRight,
  Clapperboard,
  FlaskConical,
  LayoutGrid,
  Lock,
  LogOut,
  Menu,
  Plus,
  Search,
  Smile,
  Settings2,
  Users,
  X,
} from 'lucide-react';
import { Button } from './components/ui/button';
import { Loading, Logo } from './components/common';
import { Community } from './community/Community';
import { useLab, DemoProvider, LiveProvider } from './lib/service';
import { activeStatuses, DEMO } from './shared';
import { executorStatus } from './lib/production-state';
import { Home, NewSeries } from './screens/Home';
import { Production } from './screens/Production';
import { CastLibrary } from './screens/CastLibrary';
import { Experiment } from './screens/Experiment';
import { Settings } from './screens/Settings';
import { Series } from './series/Series';
import { Kids } from './kids/Kids';
import { canEdit, money, BUDGET_CAP } from '@drama/domain';

/**
 * Application chrome: navigation, the page switch, and the provider that decides whether
 * the workspace talks to Convex or to the in-memory demo.
 */
export function Sidebar({
  page,
  onCreate,
  onNavigate,
}: {
  page: string;
  onCreate: () => void;
  onNavigate: () => void;
}) {
  const { home, isDemo } = useLab();
  const links = [
    ['/', 'Your series', LayoutGrid],
    ['/cast', 'Cast library', Users],
    ['/production', 'Production queue', Clapperboard],
    ['/experiment', 'Experiment', FlaskConical],
    ['/kids', 'CLAPTAL Kids', Smile],
  ] as const;
  const b = home?.budget;
  const executor = executorStatus(home, isDemo);
  return (
    <aside className="sidebar" id="workspace-navigation">
      <Link to="/" className="logo-link" onClick={onNavigate} aria-label="CLAPTAL Drama Lab home">
        <Logo />
      </Link>
      <Link to="/settings" className="workspace-switch" onClick={onNavigate}>
        <span className="workspace-icon">D</span>
        <span>
          Director’s workspace<small>PRIVATE STUDIO</small>
        </span>
        <ChevronDown size={14} />
      </Link>
      <span className="nav-label">WORKSPACE</span>
      <nav aria-label="Workspace navigation">
        {links.map(([to, title, Icon]) => (
          <Link
            key={to}
            to={to}
            onClick={onNavigate}
            aria-current={
              page === to || (to === '/' && page.startsWith('/series/')) ? 'page' : undefined
            }
            className={`nav-item ${page === to || (to === '/' && page.startsWith('/series/')) ? 'active' : ''}`}
          >
            <Icon size={18} />
            <span>{title}</span>
            {to === '/production' &&
              !!home?.commands.filter((c) => activeStatuses.includes(c.status)).length && (
                <b>{home.commands.filter((c) => activeStatuses.includes(c.status)).length}</b>
              )}
          </Link>
        ))}
        <span className="mobile-session">
          <Link to="/settings" className="nav-item" aria-label="Workspace settings">
            <Settings2 size={18} />
          </Link>
          {!isDemo && <SignOut />}
        </span>
      </nav>
      {home?.access.role === 'owner' && (
        <div className="community-navigation">
          <span className="nav-label">COMMUNITY</span>
          <nav aria-label="Community navigation">
            {(
              [
                ['/community', 'Overview'],
                ['/community/ideas', 'Ideas'],
                ['/community/people', 'People'],
                ['/community/voting', 'Voting & winners'],
                ['/community/moderation', 'Moderation'],
              ] as const
            ).map(([to, title]) => (
              <Link
                key={to}
                to={to}
                activeOptions={{ exact: true, includeSearch: false }}
                search={{
                  qstate: 'pending',
                  q: '',
                  filter: '',
                  period: '',
                  genre: '',
                  past: 'false',
                }}
                onClick={onNavigate}
                aria-current={page === to ? 'page' : undefined}
                className={`nav-item ${page === to ? 'active' : ''}`}
              >
                <Users size={17} />
                <span>{title}</span>
              </Link>
            ))}
          </nav>
        </div>
      )}
      <Button
        variant="ghost"
        onClick={() => {
          onNavigate();
          onCreate();
        }}
        className="sidebar-create"
        disabled={home && !canEdit(home.access.role)}
      >
        <Plus size={16} />
        New series
      </Button>
      <div className="sidebar-bottom">
        <div className="budget-card">
          <div>
            <span>Experiment budget</span>
            <span className="budget-icon">
              <FlaskConical size={14} />
            </span>
          </div>
          {b?.trial && (
            <small role="status">
              Pilot project: {money(Math.max(0, home?.trialRemaining ?? 0))} available of{' '}
              {money(b.trial.cap)}
            </small>
          )}
          <p>
            {money(b ? Math.max(0, b.cap - b.spent - b.reserved) : BUDGET_CAP)}
            <small> remaining</small>
          </p>
          <div className="budget-track">
            <i
              style={{ width: `${b ? Math.min(100, ((b.spent + b.reserved) / b.cap) * 100) : 0}%` }}
            />
          </div>
          <small>
            {isDemo
              ? 'Simulated workspace · no charges'
              : `${money(b?.cap ?? BUDGET_CAP)} cap · usage tracked per shot`}
          </small>
        </div>
        <div className="local-status">
          <span className={`connection-dot ${home?.workerOnline ? 'online' : ''}`} />
          <span>
            {executor.title}
            <small>{executor.description}</small>
          </span>
        </div>
        <Link
          to="/settings"
          onClick={onNavigate}
          className={`nav-item settings-link ${page === '/settings' ? 'active' : ''}`}
        >
          <Settings2 size={17} />
          <span>Workspace settings</span>
        </Link>
        <div className="user-row">
          <span className="avatar">{home?.access.name?.[0] ?? 'D'}</span>
          <span>
            {home?.access.name ?? 'Director'}
            <small>{home?.access.role ?? 'Private workspace'}</small>
          </span>
          {isDemo ? <span className="demo-label">DEMO</span> : <SignOut />}
        </div>
      </div>
    </aside>
  );
}
export function SignOut() {
  const { signOut } = useAuthActions();
  return (
    <Button variant="ghost" size="icon" onClick={() => void signOut()} aria-label="Sign out">
      <LogOut size={16} />
    </Button>
  );
}
export function Shell({ page }: { page: string }) {
  const [create, setCreate] = useState(false),
    [search, setSearch] = useState(''),
    [error, setError] = useState(''),
    [mobileOpen, setMobileOpen] = useState(false);
  const lab = useLab();
  const navigationToggle = useRef<HTMLButtonElement>(null);
  const onError = (e: unknown) => {
    setError(e instanceof Error ? e.message : String(e));
  };
  return (
    <div
      className={`app-shell ${page.startsWith('/community') ? 'community-shell' : ''} ${mobileOpen ? 'navigation-open' : ''}`}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && mobileOpen) {
          setMobileOpen(false);
          navigationToggle.current?.focus();
        }
      }}
    >
      <div className="main-shell">
        <header className="topbar">
          <div className="mobile-brand">
            <Logo />
            <Button
              ref={navigationToggle}
              variant="ghost"
              size="icon"
              aria-label={mobileOpen ? 'Close workspace navigation' : 'Open workspace navigation'}
              aria-controls="workspace-navigation"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </Button>
          </div>
          <div className="breadcrumb">
            {page.startsWith('/community') ? 'Community' : 'Workspace'}
            <ChevronRight size={13} />
            <strong>
              {page.startsWith('/community')
                ? (
                    {
                      '/community': 'Overview',
                      '/community/ideas': 'Ideas',
                      '/community/people': 'People',
                      '/community/voting': 'Voting & winners',
                      '/community/moderation': 'Moderation',
                    } as Record<string, string>
                  )[page]
                : page.startsWith('/series/')
                  ? 'Series studio'
                  : page === '/cast'
                    ? 'Cast library'
                    : page === '/kids'
                      ? 'CLAPTAL Kids'
                      : page === '/production'
                        ? 'Production queue'
                        : page === '/experiment'
                          ? 'Experiment'
                          : page === '/settings'
                            ? 'Settings'
                            : 'Your series'}
            </strong>
          </div>
          <div className="topbar-right">
            {page === '/' && (
              <label className="search">
                <Search size={16} />
                <input
                  placeholder="Find a story…"
                  aria-label="Search series"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
            )}
            <span className="private-label">
              <Lock size={12} />
              {page.startsWith('/community') ? 'Owner only' : 'Private workspace'}
            </span>
          </div>
        </header>
        <Sidebar
          page={page}
          onCreate={() => setCreate(true)}
          onNavigate={() => setMobileOpen(false)}
        />
        {lab.isDemo && (
          <div className="demo-banner">
            <FlaskConical size={13} />
            <span>Interactive demo · sample stories and simulated production · no API charges</span>
          </div>
        )}
        <main>
          {!lab.home ? (
            <Loading />
          ) : page.startsWith('/community') ? (
            <Community key={page} page={page} />
          ) : page.startsWith('/series/') ? (
            <Series onError={onError} />
          ) : page === '/kids' ? (
            <Kids onError={onError} />
          ) : page === '/production' ? (
            <Production onError={onError} />
          ) : page === '/cast' ? (
            <CastLibrary />
          ) : page === '/experiment' ? (
            <Experiment />
          ) : page === '/settings' ? (
            <Settings onError={onError} />
          ) : (
            <Home onCreate={() => setCreate(true)} search={search} />
          )}
        </main>
      </div>
      {create && <NewSeries onClose={() => setCreate(false)} onError={onError} />}
      {error && (
        <div className="toast" role="alert">
          <span>{error}</span>
          <button aria-label="Dismiss message" onClick={() => setError('')}>
            <X size={17} />
          </button>
        </div>
      )}
    </div>
  );
}
export function Application() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const projectId = path.startsWith('/series/') ? path.split('/')[2] : undefined;
  return DEMO ? (
    <DemoProvider projectId={projectId}>
      <Shell page={path} />
    </DemoProvider>
  ) : (
    <LiveProvider projectId={projectId}>
      <Shell page={path} />
    </LiveProvider>
  );
}
